"use client"

/**
 * DockableLayout — the main docking workspace manager.
 *
 * Wraps flexlayout-react's <Layout> component and provides:
 *  • Persistence: saves/restores user layout from sessionStorage (per tab/session)
 *  • Theme integration: applies light/dark class from next-themes
 *  • Layout reset: restores the default 4-panel layout
 *  • Tab customization: icons and close behavior
 *
 * Usage:
 *   <DockableLayout />
 *
 * This component should fill 100% of its parent container.
 */

import { useCallback, useRef, useState, useEffect } from "react"
import { hasAuthSession } from "@/lib/auth-client"
import {
  Layout,
  Model,
  Actions,
  DockLocation,
  type TabNode,
  type TabSetNode,
  type BorderNode,
  type ITabSetRenderValues,
  type IJsonModel,
  type Action,
} from "flexlayout-react"
import { basename, isImageFile, normalizePath } from "@/lib/compiler/path-utils"
import { useTheme } from "next-themes"
import { Plus, RotateCcw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { panelFactory } from "./panel-factory"
import {
  getDefaultLayoutJson,
  LAYOUT_STORAGE_KEY,
  LAYOUT_LAST_ACTIVE_KEY,
  LAYOUT_INACTIVITY_MS,
  LAYOUT_SCHEMA_KEY,
  LAYOUT_SCHEMA_VERSION,
  LAYOUT_EDITOR_PLACEHOLDER_FILE_ID,
  PANEL_COMPONENTS,
  PDF_TABSET_ID,
  type PanelComponent,
} from "./layout-model"
import { TabListPopover } from "./tab-list-popover"
import { EditorTabBar } from "../tabs/EditorTabBar"
import type { EditorTab } from "../tabs/types"
import { useEditorStore } from "@/lib/editor-store"
import type { DetachableTabData } from "@/lib/detached-tab-sync"
import { DETACHED_TAB_DRAG_MIME } from "@/lib/detached-tab-sync"
import { openOrAddDetachedTab } from "@/lib/detached-window-utils"
import { useLayoutPanels } from "./layout-context"
import { addEditorTabToTabset, editorTabIdFromFileId } from "./editor-tab"
import { detectDirection, dockRectFor, ZONE_CENTERS, COMPASS_WIDTH, COMPASS_HEIGHT, type SplitDirection } from "./split-compass"
import {
  getClosedPanels,
  getCodeEditorTabsetId,
  ensureEditorTabsetId,
  reopenPanel as reopenPanelInModel,
  normalizeEditorConsoleLayout,
  removeLayoutPlaceholderTabs,
} from "./layout-panel-utils"
import {
  EDITOR_TABSET_ID,
} from "./layout-model"

/* ── helpers ── */

const SPLIT_DIRECTION_TO_DOCK: Record<SplitDirection, DockLocation> = {
  top: DockLocation.TOP,
  bottom: DockLocation.BOTTOM,
  left: DockLocation.LEFT,
  right: DockLocation.RIGHT,
  center: DockLocation.CENTER,
}

const DRAG_PREVIEW_WIDTH = 320
const DRAG_PREVIEW_PANEL_MIN_HEIGHT = 320
const DRAG_PREVIEW_MAX_LINES = 15
const DRAG_PREVIEW_LINE_HEIGHT = 21
const DRAG_PREVIEW_CODE_PADDING_Y = 12

function dragPreviewIconSrc(fileName: string): string {
  return isImageFile(fileName) ? "/fileIcon.svg" : "/codeFileIcon.svg"
}

/**
 * Builds the tab chip + line-numbered code excerpt shown by the floating
 * drag ghost below — a small mock-up rendered from the dragged tab's actual
 * cached text content, rather than a screenshot of the real DOM. An earlier
 * version screenshotted the tabset's live rendered content, but that only
 * works for the tabset's currently-*selected* tab — FlexLayout keeps each
 * tab's rendered content in one shared, reparented DOM node (its
 * "moveable"), and only the selected tab's moveable is actually on screen to
 * snapshot. That produced a visibly different preview for the selected tab
 * (a scaled screenshot with real chrome) vs. every other tab in the same bar
 * (plain text). Building both from the same text-based mock-up keeps the
 * preview identical no matter which tab was selected beforehand.
 *
 * The tab chip and the code panel are two independently-backed pieces (not
 * one shared card background) rather than a single continuous card: an
 * earlier version tried to fake a "notch" cut into one continuous card
 * background via a radial-gradient curve, but that curve was only ever
 * revealing the *card's own* opaque white fill underneath it — never
 * actually see-through — so it read as a plain rounded rectangle. The tab
 * now sits flush on top of the panel's own top edge (overlapping it by a
 * hairline to hide the seam, see CSS) rather than floating above it with a
 * gap, so the two read as one attached shape while the area beside the tab
 * — outside both elements' actual boxes — has nothing painted into it and
 * is genuinely transparent.
 */
function buildDragTab(title: string, iconSrc: string): HTMLElement {
  const tab = document.createElement("div")
  tab.className = "fl-drag-preview-tab"
  const icon = document.createElement("img")
  icon.className = "fl-drag-preview-tab-icon"
  icon.src = iconSrc
  icon.alt = ""
  const name = document.createElement("span")
  name.className = "fl-drag-preview-tab-name"
  name.textContent = title
  tab.append(icon, name)
  return tab
}

function buildDragGhostRoot(tab: HTMLElement, panel: HTMLElement): HTMLElement {
  const root = document.createElement("div")
  root.className = "fl-drag-preview-root"
  root.style.width = `${DRAG_PREVIEW_WIDTH}px`
  root.append(tab, panel)
  return root
}

function clampPreviewLines(rawText: string, emptyLabel: string): string[] {
  const rawLines = rawText.replace(/\s+$/, "").split("\n")
  return (rawLines.length === 1 && rawLines[0] === "" ? [emptyLabel] : rawLines).slice(0, DRAG_PREVIEW_MAX_LINES)
}

/** Line-numbered text excerpt panel — shared by code-editor and console tabs. */
function buildLinesPanel(lines: string[]): HTMLElement {
  const panel = document.createElement("div")
  panel.className = "fl-drag-preview-panel"
  const panelContentHeight = lines.length * DRAG_PREVIEW_LINE_HEIGHT + DRAG_PREVIEW_CODE_PADDING_Y * 2
  panel.style.height = `${Math.max(panelContentHeight, DRAG_PREVIEW_PANEL_MIN_HEIGHT)}px`

  const code = document.createElement("div")
  code.className = "fl-drag-preview-code"
  const gutter = document.createElement("div")
  gutter.className = "fl-drag-preview-gutter"
  gutter.textContent = lines.map((_, i) => String(i + 1)).join("\n")
  const codeLines = document.createElement("pre")
  codeLines.className = "fl-drag-preview-lines"
  codeLines.textContent = lines.join("\n")
  code.append(gutter, codeLines)
  panel.appendChild(code)
  return panel
}

/** Non-text panel placeholder (PDF viewer, no cached thumbnail yet) — there's no line content to excerpt, just an icon + label. */
function buildPlaceholderPanel(iconSrc: string, label: string): HTMLElement {
  const panel = document.createElement("div")
  panel.className = "fl-drag-preview-panel fl-drag-preview-placeholder"
  panel.style.height = `${DRAG_PREVIEW_PANEL_MIN_HEIGHT}px`
  const icon = document.createElement("img")
  icon.className = "fl-drag-preview-placeholder-icon"
  icon.src = iconSrc
  icon.alt = ""
  const text = document.createElement("span")
  text.className = "fl-drag-preview-placeholder-label"
  text.textContent = label
  panel.append(icon, text)
  return panel
}

/** Page-thumbnail panel (PDF viewer) — shows a data URL cached from the Flutter viewer's own PDF_THUMBNAIL postMessage. */
function buildImagePanel(dataUrl: string): HTMLElement {
  const panel = document.createElement("div")
  panel.className = "fl-drag-preview-panel fl-drag-preview-image-panel"
  panel.style.height = `${DRAG_PREVIEW_PANEL_MIN_HEIGHT}px`
  const img = document.createElement("img")
  img.className = "fl-drag-preview-image"
  img.src = dataUrl
  img.alt = ""
  panel.appendChild(img)
  return panel
}

/**
 * Builds the tab chip + content-excerpt shown by the floating drag ghost
 * below — a small mock-up rendered from the dragged tab's actual cached
 * content, rather than a screenshot of the real DOM. An earlier version
 * screenshotted the tabset's live rendered content, but that only works for
 * the tabset's currently-*selected* tab — FlexLayout keeps each tab's
 * rendered content in one shared, reparented DOM node (its "moveable"), and
 * only the selected tab's moveable is actually on screen to snapshot. That
 * produced a visibly different preview for the selected tab (a scaled
 * screenshot with real chrome) vs. every other tab in the same bar (plain
 * text). Building both from the same text-based mock-up keeps the preview
 * identical no matter which tab was selected beforehand.
 *
 * The tab chip and the panel are two independently-backed pieces (not one
 * shared card background) rather than a single continuous card: an earlier
 * version tried to fake a "notch" cut into one continuous card background
 * via a radial-gradient curve, but that curve was only ever revealing the
 * *card's own* opaque white fill underneath it — never actually see-through
 * — so it read as a plain rounded rectangle. The tab now sits flush on top
 * of the panel's own top edge (overlapping it by a hairline to hide the
 * seam, see CSS) rather than floating above it with a gap, so the two read
 * as one attached shape while the area beside the tab — outside both
 * elements' actual boxes — has nothing painted into it and is genuinely
 * transparent.
 */
function buildCodeDragCard(title: string, fileContent: string): HTMLElement {
  const lines = clampPreviewLines(fileContent, "(empty file)")
  return buildDragGhostRoot(buildDragTab(title, dragPreviewIconSrc(title)), buildLinesPanel(lines))
}

/** Same tab-chip + line-numbered mock-up as buildCodeDragCard, fed from recent console output instead of file content. */
function buildConsoleDragCard(title: string, outputText: string): HTMLElement {
  const lines = clampPreviewLines(outputText, "(no output)")
  return buildDragGhostRoot(buildDragTab(title, "/codeFileIcon.svg"), buildLinesPanel(lines))
}

/**
 * PDF tabs have no cached text to excerpt, and rendering an actual page
 * thumbnail synchronously at dragstart isn't feasible — but the Flutter PDF
 * viewer pushes a thumbnail data URL proactively via postMessage whenever
 * its own preview changes (see the PDF_THUMBNAIL handler in
 * synctex-pdf-viewer.tsx), cached in state.pdfThumbnailByUrl. Use that if
 * it's arrived by drag time; otherwise fall back to the icon + label
 * placeholder (e.g. before the PDF has ever finished loading).
 */
function buildPdfDragCard(title: string, thumbnailDataUrl: string | undefined): HTMLElement {
  const panel = thumbnailDataUrl
    ? buildImagePanel(thumbnailDataUrl)
    : buildPlaceholderPanel("/im_pdf.svg", "PDF Preview")
  return buildDragGhostRoot(buildDragTab(title, "/im_pdf.svg"), panel)
}

/**
 * Native drag images (`dataTransfer.setDragImage`) are rendered by Windows
 * Chromium with a fixed, unconfigurable alpha baked into its own drag-ghost
 * compositing — nothing on the source element's side (including stacking
 * multiple opaque copies, which was tried and made no difference: the
 * browser flattens the element to one bitmap *before* applying that alpha,
 * so extra internal layers don't compound anything) can make that bitmap
 * come out fully opaque. The only reliable fix is to not use a native drag
 * image for the visible preview at all: hand the browser a blank 1x1 canvas
 * (so nothing native is drawn) and instead render our own plain, always-
 * on-screen DOM element that follows the pointer — a normal painted node,
 * outside setDragImage's pipeline, shows at full opacity like anything else
 * on the page.
 */
function suppressNativeDragImage(dataTransfer: DataTransfer) {
  const blank = document.createElement("canvas")
  blank.width = 1
  blank.height = 1
  dataTransfer.setDragImage(blank, 0, 0)
}

function touchLayoutActivity() {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(LAYOUT_LAST_ACTIVE_KEY, String(Date.now()))
  } catch {
    // ignore
  }
}

function isLayoutSessionExpired(): boolean {
  if (typeof window === "undefined") return false
  try {
    const last = sessionStorage.getItem(LAYOUT_LAST_ACTIVE_KEY)
    if (!last) return false
    return Date.now() - Number(last) > LAYOUT_INACTIVITY_MS
  } catch {
    return false
  }
}

/** Auth layouts are stored per project so a previous anonymous session cannot leak in. */
function layoutStorageKey(projectId: string): string {
  return projectId ? `${LAYOUT_STORAGE_KEY}:${projectId}` : LAYOUT_STORAGE_KEY
}

function clearLayoutSession(projectId = "") {
  if (typeof window === "undefined") return
  try {
    sessionStorage.removeItem(layoutStorageKey(projectId))
    if (projectId) sessionStorage.removeItem(LAYOUT_STORAGE_KEY)
    sessionStorage.removeItem(LAYOUT_LAST_ACTIVE_KEY)
    sessionStorage.removeItem(LAYOUT_SCHEMA_KEY)
    // Remove legacy cross-session persistence from older builds
    localStorage.removeItem(LAYOUT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/** Try to load a persisted layout from sessionStorage (same tab/session only) */
function loadPersistedModel(projectId: string): Model | null {
  if (typeof window === "undefined") return null
  // No project id yet — do not restore the unscoped anonymous layout into an auth session.
  if (hasAuthSession() && !projectId) return null

  // Drop legacy localStorage layout so old sessions don't keep a 1-panel state forever
  try {
    localStorage.removeItem(LAYOUT_STORAGE_KEY)
  } catch {
    // ignore
  }

  if (isLayoutSessionExpired()) {
    clearLayoutSession(projectId)
    return null
  }

  try {
    const schema = sessionStorage.getItem(LAYOUT_SCHEMA_KEY)
    if (schema !== String(LAYOUT_SCHEMA_VERSION)) {
      clearLayoutSession(projectId)
      return null
    }

    const raw = sessionStorage.getItem(layoutStorageKey(projectId))
    if (!raw) return null
    const json: IJsonModel = JSON.parse(raw)
    return Model.fromJson(json)
  } catch {
    return null
  }
}

/** Save the current model to sessionStorage */
function persistModel(model: Model, projectId: string) {
  if (typeof window === "undefined") return
  try {
    touchLayoutActivity()
    sessionStorage.setItem(layoutStorageKey(projectId), JSON.stringify(model.toJson()))
    sessionStorage.setItem(LAYOUT_SCHEMA_KEY, String(LAYOUT_SCHEMA_VERSION))
  } catch {
    // quota exceeded or SSR — ignore
  }
}

function modelHasRealEditorTab(model: Model): boolean {
  let found = false
  model.visitNodes((node) => {
    if (found || node.getType() !== "tab") return
    const tab = node as TabNode
    const component = tab.getComponent()
    if (component !== PANEL_COMPONENTS.CODE_EDITOR && component !== PANEL_COMPONENTS.IMAGE) return
    const fileId = (tab.getConfig() as { fileId?: string } | undefined)?.fileId
    if (fileId && fileId !== LAYOUT_EDITOR_PLACEHOLDER_FILE_ID) found = true
  })
  return found
}

/* ── component ── */

export function DockableLayout() {
  const { resolvedTheme } = useTheme()
  const layoutRef = useRef<Layout>(null)
  const [model, setModel] = useState<Model | null>(null)
  const { registerOpenFileHandler, registerPathEventHandler, getContent, state, setActiveFile, openFile, projectId } = useEditorStore()
  const { registerLayoutApi, notifyLayoutChanged, setActivePanelComponent, setActiveTabId } = useLayoutPanels()
  const modelRef = useRef<Model | null>(null)
  modelRef.current = model
  const layoutProjectIdRef = useRef(projectId)
  layoutProjectIdRef.current = projectId
  const didRestoreActiveTabRef = useRef(false)

  /* Initialise model (client-side only) */
  useEffect(() => {
    // Restore this project's own layout. The unscoped key is anonymous-only,
    // so a previous non-auth main.tex cannot be loaded into an auth project.
    const persisted = loadPersistedModel(projectId)
    const initial = persisted ?? Model.fromJson(getDefaultLayoutJson())
    normalizeEditorConsoleLayout(initial)
    setModel(initial)
    updateActivePanelComponent(initial)
    if (persisted) {
      touchLayoutActivity()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Keep the live editor tabs when the user returns. Socket recovery runs
     separately and must not replace this model with the empty placeholder. */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return
      touchLayoutActivity()
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [])

  /* Register the open-file handler so the explorer can add tabs */
  useEffect(() => {
    if (!model) return
    registerOpenFileHandler((fileId: string, fileName: string) => {
      // Check if a tab for this file is already open — match by exact fileId OR by fileName/basename
      let existingTabId: string | null = null
      const targetBasename = fileName.toLowerCase()
      model.visitNodes((node) => {
        if (node.getType() === "tab") {
          const tabNode = node as TabNode
          const cfg = tabNode.getConfig()
          const component = tabNode.getComponent()
          if (component === PANEL_COMPONENTS.CODE_EDITOR || component === PANEL_COMPONENTS.IMAGE) {
            const tabFileId: string = cfg?.fileId ?? ""
            if (
              tabFileId === fileId ||
              tabFileId.toLowerCase() === fileId.toLowerCase() ||
              tabNode.getName().toLowerCase() === targetBasename ||
              tabFileId.split("/").pop()?.toLowerCase() === targetBasename
            ) {
              existingTabId = tabNode.getId()
            }
          }
        }
      })


      if (existingTabId) {
        const tab = model.getNodeById(existingTabId) as TabNode | undefined
        const cfg = tab?.getConfig() as { fileId?: string } | undefined
        const tabFileId = cfg?.fileId ? normalizePath(cfg.fileId) : ""
        if (cfg && tabFileId !== normalizePath(fileId)) {
          model.doAction(Actions.renameTab(existingTabId, fileName))
          model.doAction(
            Actions.updateNodeAttributes(existingTabId, {
              config: { ...cfg, fileId },
            }),
          )
        }
        model.doAction(Actions.selectTab(existingTabId))
      } else {
        const editorTabsetId = ensureEditorTabsetId(model)
        const component = isImageFile(fileId) ? PANEL_COMPONENTS.IMAGE : PANEL_COMPONENTS.CODE_EDITOR
        model.doAction(
          Actions.addNode(
            {
              type: "tab",
              id: editorTabIdFromFileId(fileId),
              component: component,
              name: fileName,
              config: { fileId },
            },
            editorTabsetId,
            DockLocation.CENTER,
            -1,
            true,
          ),
        )
        removeLayoutPlaceholderTabs(model)
      }
    })
  }, [model, registerOpenFileHandler])

  /* If a remount started from the empty placeholder, open the file the store
     already considers active. Runs after the handler above is registered.
     Once per mount, so closing the last tab does not immediately reopen it. */
  useEffect(() => {
    if (!model || didRestoreActiveTabRef.current) return
    const active = state.activeFile
    if (!active || active === LAYOUT_EDITOR_PLACEHOLDER_FILE_ID) return
    if (modelHasRealEditorTab(model)) {
      didRestoreActiveTabRef.current = true
      return
    }
    didRestoreActiveTabRef.current = true
    openFile(active, basename(active))
  }, [model, state.activeFile, openFile])

  /* Register rename/delete handler to keep layout tabs in sync */
  useEffect(() => {
    if (!model) return
    return registerPathEventHandler((event) => {
      touchLayoutActivity()
      if (event.type === "rename") {
        const { source, target } = event
        model.visitNodes((node) => {
          if (node.getType() === "tab") {
            const tabNode = node as TabNode
            const cfg = tabNode.getConfig() as { fileId?: string } | undefined
            const tabFileId = cfg?.fileId ? normalizePath(cfg.fileId) : ""
            if (tabFileId === source || tabFileId.startsWith(`${source}/`)) {
              const suffix = tabFileId === source ? "" : tabFileId.slice(source.length)
              const newFileId = `${target}${suffix}`
              const newName = basename(newFileId)
              model.doAction(Actions.renameTab(tabNode.getId(), newName))
              model.doAction(
                Actions.updateNodeAttributes(tabNode.getId(), {
                  config: { ...cfg, fileId: newFileId },
                })
              )
            }
          }
        })
      } else if (event.type === "delete") {
        const { path } = event
        const tabsToDelete: string[] = []
        model.visitNodes((node) => {
          if (node.getType() === "tab") {
            const tabNode = node as TabNode
            const cfg = tabNode.getConfig() as { fileId?: string } | undefined
            const tabFileId = cfg?.fileId ? normalizePath(cfg.fileId) : ""
            if (tabFileId === path || tabFileId.startsWith(`${path}/`)) {
              tabsToDelete.push(tabNode.getId())
            }
          }
        })
        tabsToDelete.forEach((tabId) => {
          model.doAction(Actions.deleteTab(tabId))
        })
      }
    })
  }, [model, registerPathEventHandler])

  /* ── PDF tab: rename placeholder when active editor file changes ── */
  useEffect(() => {
    if (!model || !state.activeFile?.endsWith(".tex")) return
    const isAuth = hasAuthSession()
    const tabName = isAuth
      ? basename(state.activeFile).replace(/\.tex$/, ".pdf")
      : "main.pdf"
    model.visitNodes((node) => {
      if (node.getType() !== "tab") return
      const tab = node as TabNode
      if (tab.getComponent() !== PANEL_COMPONENTS.PDF_PREVIEW) return
      const cfg = tab.getConfig() ?? {}
      // Only rename uncompiled placeholder tabs (no compiledFile in config yet)
      if (!cfg.compiledFile) {
        model.doAction(Actions.renameTab(tab.getId(), tabName))
      }
    })
  }, [model, state.activeFile])

  /* ── PDF tab: add/switch tab when compile succeeds for a tex file ── */
  const lastPdfRef = useRef<{ compiledFile: string; pdfUrl: string } | null>(null)
  useEffect(() => {
    if (!model) return
    const { compiledFile, pdfUrl } = state
    if (!compiledFile || !pdfUrl) return
    if (
      lastPdfRef.current?.compiledFile === compiledFile &&
      lastPdfRef.current?.pdfUrl === pdfUrl
    ) return
    lastPdfRef.current = { compiledFile, pdfUrl }

    const tabName = basename(compiledFile).replace(/\.tex$/, ".pdf")

    // Find existing compiled PDF tab for this file, and any placeholder tab
    let existingTabId: string | null = null
    let placeholderTabId: string | null = null
    let pdfTabsetId: string | null = null

    model.visitNodes((node) => {
      if (node.getType() !== "tab") return
      const tab = node as TabNode
      if (tab.getComponent() !== PANEL_COMPONENTS.PDF_PREVIEW) return
      const cfg = tab.getConfig() ?? {}
      if (!pdfTabsetId) pdfTabsetId = tab.getParent()?.getId() ?? null
      if (cfg.compiledFile === compiledFile) existingTabId = tab.getId()
      else if (!cfg.compiledFile) placeholderTabId = tab.getId()
    })

    const newConfig = { compiledFile, pdfUrl }

    if (placeholderTabId) {
      // Upgrade the placeholder tab to a dedicated compiled tab, without selecting it
      model.doAction(Actions.renameTab(placeholderTabId, tabName))
      model.doAction(Actions.updateNodeAttributes(placeholderTabId, { config: newConfig }))
    } else if (!existingTabId) {
      // Add a brand-new PDF tab to whichever tabset already holds PDF panels,
      // without switching focus/ribbon to it
      const targetTabsetId = pdfTabsetId ?? PDF_TABSET_ID
      model.doAction(
        Actions.addNode(
          {
            type: "tab",
            component: PANEL_COMPONENTS.PDF_PREVIEW,
            name: tabName,
            config: newConfig,
          },
          targetTabsetId,
          DockLocation.CENTER,
          -1,
          false,
        ),
      )
    }
    // else: tab for this file already exists — content (pdfByFile) stays fresh
    // via config alone, so there's nothing to do without stealing focus from
    // whatever the user is looking at.
  }, [model, state.compiledFile, state.pdfUrl])

  /* Ref for the layout wrapper div */
  const wrapperRef = useRef<HTMLDivElement>(null)

  /* Currently-dragged tab, shared between the detach-drag and split-compass
   * effects below (set on native dragstart, cleared on dragend/drop). */
  const draggedTabRef = useRef<TabNode | null>(null)

  /* The floating drag-preview element (see buildCodeDragCard/buildConsoleDragCard/
   * buildPdfDragCard/suppressNativeDragImage) currently following the
   * pointer, if any — set on dragstart, repositioned on every dragover,
   * removed on dragend/drop. */
  const dragGhostRef = useRef<HTMLElement | null>(null)

  /* Split-compass's current verdict for the in-flight drag — read by
   * handleAction below to override/cancel the FlexLayout_MoveNode action
   * FlexLayout's own (unsuppressed) drop handling generates. null whenever
   * the pointer isn't over another tabset's content area at all, meaning
   * FlexLayout is left to handle the action itself (tab reordering,
   * tabset-header moves, edge drops). */
  const pendingSplitHitRef = useRef<{ tabsetId: string; direction: SplitDirection | null } | null>(null)

  /* ── factory wrapper (flexlayout calls this for every tab) ── */
  const factory = useCallback((node: TabNode) => {
    return panelFactory(node)
  }, [])

  /* Whichever tab last had focus — the active tabset's selected tab — drives
   * editor-ribbon-bar.tsx's choice between the Word-style ribbon and the PDF
   * toolbar. Recomputed on every model change (tab select, close, split, …). */
  const updateActivePanelComponent = useCallback(
    (m: Model) => {
      const selected = m.getActiveTabset()?.getSelectedNode()
      const tab = selected?.getType() === "tab" ? (selected as TabNode) : null
      setActivePanelComponent(tab ? (tab.getComponent() as PanelComponent) : null)
      setActiveTabId(tab?.getId() ?? null)
    },
    [setActivePanelComponent, setActiveTabId],
  )

  /* ── persist on every layout change ── */
  const handleModelChange = useCallback(
    (m: Model) => {
      persistModel(m, layoutProjectIdRef.current)
      notifyLayoutChanged()
      updateActivePanelComponent(m)
    },
    [notifyLayoutChanged, updateActivePanelComponent],
  )

  /* ── intercept actions ── */
  const handleAction = useCallback((action: Action): Action | undefined => {
    // Split-compass override: FlexLayout's own drag handling is left
    // unsuppressed (see the split-compass effect below — stopping its
    // dragover/drop propagation was found to also break its dragend-driven
    // cleanup), so it generates its own FlexLayout_MoveNode from wherever
    // it thinks the drop landed. Whenever the pad was actively governing
    // the hover, replace that with the pad's own verdict — or cancel the
    // action outright if the pointer wasn't over one of the pad's icons.
    if (action.type === "FlexLayout_MoveNode") {
      const pending = pendingSplitHitRef.current
      if (pending) {
        pendingSplitHitRef.current = null
        if (!pending.direction) {
          return undefined
        }
        const data = action.data as { fromNode: string; select?: boolean }
        const draggedTab = draggedTabRef.current
        if (!draggedTab) {
            return undefined
        }
        const fromNodeId = draggedTab.getId()
        const result = Actions.moveNode(
          fromNodeId,
          pending.tabsetId,
          SPLIT_DIRECTION_TO_DOCK[pending.direction],
          -1,
          data.select ?? true,
        )
        return result
      }
    }
    // When the user clicks a code-editor tab, sync activeFile in the store so
    // compile always targets the currently visible tex file.
    if (action.type === "FlexLayout_SelectTab" && model) {
      const tabId = (action.data as { tabNode?: string }).tabNode
      if (tabId) {
        const node = model.getNodeById(tabId)
        if (node?.getType() === "tab") {
          const tab = node as TabNode
          if (tab.getComponent() === PANEL_COMPONENTS.CODE_EDITOR) {
            const fileId: string = (tab.getConfig() as { fileId?: string })?.fileId ?? ""
            if (fileId) setActiveFile(fileId)
          }
        }
      }
    }
    return action
  }, [model, setActiveFile])

  // Handle external drag start from EditorTabBar (for floating preview ghost)
  const handleEditorTabExternalDragStart = useCallback((e: React.DragEvent, index: number, tabsetId: string) => {
    // Look up the TabNode from the model and set draggedTabRef
    // so the global dragstart listener can find it and build the preview
    if (!model) {
      return
    }
    const tabsetNode = model.getNodeById(tabsetId)
    if (!tabsetNode) {
      return
    }
    if (tabsetNode.getType() !== "tabset") {
      return
    }
    const children = (tabsetNode as TabSetNode).getChildren() as TabNode[]
    if (index >= 0 && index < children.length) {
      draggedTabRef.current = children[index]
    } else {
    }
  }, [model])

  // Reference to the compass processDragOver function will be set by the effect
  const compassProcessDragOverRef = useRef<((clientX: number, clientY: number) => void) | null>(null)

  // Handle dragover events from EditorTabBar to show compass
  const handleEditorTabExternalDragOver = useCallback((clientX: number, clientY: number) => {
    if (compassProcessDragOverRef.current) {
      compassProcessDragOverRef.current(clientX, clientY)
    }
  }, [])

  /* ── customise tabset header using EditorTabBar ── */
  const handleRenderTabSet = useCallback(
    (
      tabSetNode: TabSetNode | BorderNode,
      renderValues: ITabSetRenderValues,
    ) => {
      if (tabSetNode.getType() !== "tabset" || !model) return

      const tsNode = tabSetNode as TabSetNode
      const children = tsNode.getChildren() as TabNode[]
      const activeIndex = tsNode.getSelected() < 0 ? 0 : tsNode.getSelected()

      const tabs: EditorTab[] = children.map((node) => {
        return {
          id: node.getId(),
          fileName: node.getName(),
          isModified: (node.getConfig() as any)?.isModified ?? false,
        }
      })

      const handleSelectTab = (index: number) => {
        if (!model || index < 0 || index >= children.length) return
        model.doAction(Actions.selectTab(children[index].getId()))
      }

      const handleCloseTab = (index: number) => {
        if (!model || index < 0 || index >= children.length) return
        model.doAction(Actions.deleteTab(children[index].getId()))
      }

      const handleReorderTabs = (sourceIndex: number, targetIndex: number) => {
        if (!model || sourceIndex < 0 || sourceIndex >= children.length) return
        model.doAction(
          Actions.moveNode(
            children[sourceIndex].getId(),
            tsNode.getId(),
            DockLocation.CENTER,
            targetIndex,
          ),
        )
      }

      const handleClearAllTabs = () => {
        if (!model) return
        children.forEach((child) => {
          model.doAction(Actions.deleteTab(child.getId()))
        })
      }

      const isEditorTabset = tsNode.getId() === (getCodeEditorTabsetId(model) ?? EDITOR_TABSET_ID)

      const rightToolbar = (
        <div className="flex items-center gap-2 shrink-0">
          {isEditorTabset && (
            <button
              key="add-tab"
              type="button"
              title="Add tab"
              className="w-6 h-6 flex items-center justify-center rounded-[2px] border border-[var(--ed-dock-btn-border)] bg-[var(--ed-dock-btn-bg)] text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-all shadow-2xs shrink-0"
              onClick={() => {
                if (!model) return
                addEditorTabToTabset(model, tsNode.getId())
              }}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <Plus className="w-3.5 h-3.5 text-slate-600 shrink-0" />
            </button>
          )}
          {/* Vertical Column for Close (top) and Expand (bottom) matching requested layout */}
          <div className="flex flex-col gap-1 justify-center shrink-0">
            <button
              key="close-all"
              type="button"
              title="Clear all tabs"
              className="w-5 h-5 flex items-center justify-center rounded-[2px] border border-[var(--ed-dock-btn-border)] bg-[var(--ed-dock-btn-bg)] hover:bg-slate-50 transition-all shadow-2xs shrink-0"
              onClick={handleClearAllTabs}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <img
                src="/ic_close.svg"
                alt="Clear all tabs"
                className="w-[10px] h-[10px] shrink-0 object-contain"
                style={{
                  width: "10px",
                  height: "10px",
                  minWidth: "10px",
                  minHeight: "10px",
                  filter: "invert(21%) sepia(85%) saturate(5256%) hue-rotate(352deg) brightness(98%) contrast(92%)",
                }}
              />
            </button>
            <button
              key="expand"
              type="button"
              title="Expand panel"
              className="w-5 h-5 flex items-center justify-center rounded-[2px] border border-[var(--ed-dock-btn-border)] bg-[var(--ed-dock-btn-bg)] hover:bg-slate-50 transition-all shadow-2xs shrink-0"
              onClick={() => {
                if (!model) return
                model.doAction(Actions.maximizeToggle(tsNode.getId()))
              }}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <img
                src="/ic_expand.svg"
                alt="Expand"
                className="w-[10px] h-[10px] shrink-0 object-contain"
                style={{ width: "10px", height: "10px", minWidth: "10px", minHeight: "10px" }}
              />
            </button>
          </div>
        </div>
      )

      renderValues.leading = (
        <EditorTabBar
          tabs={tabs}
          activeIndex={activeIndex}
          tabsetId={tsNode.getId()}
          onSelectTab={handleSelectTab}
          onCloseTab={handleCloseTab}
          onReorderTabs={handleReorderTabs}
          onExternalDragStart={(e, index) => handleEditorTabExternalDragStart(e, index, tsNode.getId())}
          onExternalDragOver={handleEditorTabExternalDragOver}
          leftIcon={<TabListPopover tabSetNode={tsNode} model={model} />}
          rightToolbar={rightToolbar}
        />
      )
    },
    [model],
  )

  /* ── reset layout to defaults ── */
  const resetLayout = useCallback(() => {
    const fresh = Model.fromJson(getDefaultLayoutJson())
    normalizeEditorConsoleLayout(fresh)
    setModel(fresh)
    clearLayoutSession(layoutProjectIdRef.current)
    persistModel(fresh, layoutProjectIdRef.current)
  }, [])

  useEffect(() => {
    if (!model) return
    return registerLayoutApi({
      getClosedPanels: () =>
        modelRef.current ? getClosedPanels(modelRef.current) : [],
      reopenPanel: (component) => {
        if (modelRef.current) reopenPanelInModel(modelRef.current, component)
      },
      resetLayout,
    })
  }, [model, registerLayoutApi, resetLayout])

  /* ── Detach-on-drag-outside: intercept FlexLayout's native drag events ── */
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || !model) return

    let draggedTabNode: TabNode | null = null

    const buildDetachableTabData = (tabNode: TabNode): DetachableTabData => {
      const component = tabNode.getComponent() || ""
      const config = (tabNode.getConfig() ?? {}) as {
        fileId?: string
        compiledFile?: string
        pdfUrl?: string
      }

      let detachType: DetachableTabData["type"] = "editor"
      if (component === PANEL_COMPONENTS.PDF_PREVIEW) detachType = "pdf"
      else if (component === PANEL_COMPONENTS.EPUB_PREVIEW) detachType = "epub"
      else if (component === PANEL_COMPONENTS.CONSOLE) detachType = "console"

      const compiledFile = config.compiledFile
      return {
        id: tabNode.getId(),
        type: detachType,
        name: tabNode.getName(),
        component,
        fileId: config.fileId,
        compiledFile,
        pdfUrl: compiledFile
          ? (state.pdfByFile[compiledFile] ?? config.pdfUrl ?? state.pdfUrl)
          : state.pdfUrl,
        sessionId: state.sessionId,
        projectId: projectId || undefined,
        content: config.fileId ? getContent(config.fileId) : undefined,
        config: tabNode.getConfig() ?? {},
      }
    }

    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement
      const tabButton = target.closest(".flexlayout__tab_button")

      // Look for EditorTabBar tab by searching up the DOM tree
      let editorTabElement = null
      let current = target as HTMLElement | null
      while (current && !editorTabElement) {
        if (current.hasAttribute("data-editor-tab-index")) {
          editorTabElement = current
          break
        }
        current = current.parentElement
      }

      // Handle FlexLayout native tabs
      if (tabButton) {
        // Override FlexLayout's "--flexlayout--" text to prevent Chrome search
        try {
          e.dataTransfer?.setData("text/plain", "")
        } catch {
          // Some browsers restrict dataTransfer modification
        }

        // Identify which tab is being dragged by its position within its own
        // tabset's DOM tab bar, matched to that tabset's model children at the
        // same index (FlexLayout renders tab buttons in model child order, so
        // index lines up reliably). Falls
        // back to matching by name (first match) when the button's tabset
        // can't be resolved (e.g. a border rail without the marker span) —
        // that fallback picks the wrong tab whenever two open tabs share a
        // name, so it's a last resort rather than the primary path.
        draggedTabNode = null
        const tabsetEl = tabButton.closest<HTMLElement>(".flexlayout__tabset")
        const tabsetId = tabsetEl?.querySelector<HTMLElement>(".fl-tabset-id-marker")?.dataset.tabsetId
        const tabsetNode = tabsetId ? model.getNodeById(tabsetId) : null
        if (tabsetNode && tabsetNode.getType() === "tabset") {
          const buttons = Array.from(tabsetEl!.querySelectorAll<HTMLElement>(".flexlayout__tab_button"))
          const idx = buttons.indexOf(tabButton as HTMLElement)
          const children = (tabsetNode as TabSetNode).getChildren()
          if (idx >= 0 && children[idx]?.getType() === "tab") {
            draggedTabNode = children[idx] as TabNode
          }
        }

        if (!draggedTabNode) {
          const content = tabButton.querySelector(".flexlayout__tab_button_content")
          const tabName = content?.textContent?.trim() || ""
          model.visitNodes((node) => {
            if (node.getType() === "tab") {
              const tab = node as TabNode
              if (tab.getName() === tabName && !draggedTabNode) {
                draggedTabNode = tab
              }
            }
          })
        }
        draggedTabRef.current = draggedTabNode
      } else if (editorTabElement) {
        // Handle EditorTabBar tabs using data attributes
        draggedTabNode = null
        const dataset = (editorTabElement as HTMLElement).dataset
        const tabId = dataset.editorTabId
        const tabsetId = dataset.editorTabsetId

        if (tabsetId && tabId) {
          const tabsetNode = model.getNodeById(tabsetId)
          if (tabsetNode && tabsetNode.getType() === "tabset") {
            const children = (tabsetNode as TabSetNode).getChildren() as TabNode[]
            // Match by tab ID, not index, since EditorTabBar has custom stacking/ordering
            draggedTabNode = children.find(child => child.getId() === tabId) as TabNode | undefined || null
          }
        }

        draggedTabRef.current = draggedTabNode
      }
      // FlexLayout's own native outline/compass indicator (see
      // .flexlayout__outline_rect in flexlayout-overrides.css) still fires
      // for drop targets our custom split-compass doesn't cover — hovering
      // near a tab bar or a layout edge resolves to some other node it
      // considers droppable, and shows its own preview there even though
      // dropping doesn't actually do anything meaningful in this app. Since
      // our own compass + floating ghost already cover every drop that
      // *is* meaningful, suppress FlexLayout's native indicator for the
      // whole drag rather than just while our compass is governing.
      if (draggedTabNode) wrapper.classList.add("fl-tab-dragging")

      const dragged = draggedTabNode as TabNode | null
      if (dragged && e.dataTransfer) {
        try {
          e.dataTransfer.setData(
            DETACHED_TAB_DRAG_MIME,
            JSON.stringify(buildDetachableTabData(dragged)),
          )
        } catch {
          // ignore
        }
      }
    }

    const handleDragEnd = (e: DragEvent) => {
      if (!draggedTabNode) return

      const droppedOutside =
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight

      if (droppedOutside) {
        const tabData = buildDetachableTabData(draggedTabNode)

        if (tabData.type === "editor" && tabData.fileId) {
          setActiveFile(tabData.fileId)
        }
        openOrAddDetachedTab(tabData)
      }

      draggedTabNode = null
      draggedTabRef.current = null
      wrapper.classList.remove("fl-tab-dragging")
    }

    const removeDragGhost = () => {
      dragGhostRef.current?.remove()
      dragGhostRef.current = null
      // Safety net: 'dragend' is documented elsewhere in this file as not
      // always firing reliably, while 'drop' does — this listener already
      // runs on both, so mirror handleDragEnd's cleanup here too rather
      // than risk the class getting stuck on across a dragend that never
      // arrives.
      wrapper.classList.remove("fl-tab-dragging")
    }

    // Spawns the floating drag-preview element for code-editor, console, and
    // PDF-viewer tabs — bound on `document` (rather than alongside
    // handleDragStart on `wrapper`) so it fires *after* FlexLayout's own
    // default setDragImage call: that call happens inside FlexLayout's own
    // React-bound onDragStart (on the tab button itself), reached once the
    // native event bubbles all the way up to wherever React's root listener
    // sits — later than `wrapper`, but always before `document`, the last
    // possible bubble stop. Native dataTransfer.setDragImage has "last call
    // wins" semantics within a single dragstart dispatch, so firing last
    // here is what makes ours the one that actually takes effect (a blank
    // pixel — see suppressNativeDragImage) instead of FlexLayout's own ghost.
    const handleDragStartSetImage = (e: DragEvent) => {
      const dragged = draggedTabRef.current
      if (!dragged || !e.dataTransfer) return

      const component = dragged.getComponent()
      let ghost: HTMLElement | null = null
      if (component === PANEL_COMPONENTS.CODE_EDITOR) {
        const fileId = (dragged.getConfig() as { fileId?: string } | undefined)?.fileId
        ghost = buildCodeDragCard(dragged.getName(), fileId ? getContent(fileId) : "")
      } else if (component === PANEL_COMPONENTS.CONSOLE) {
        ghost = buildConsoleDragCard(dragged.getName(), state.events.map((ev) => ev.message).join("\n"))
      } else if (component === PANEL_COMPONENTS.PDF_PREVIEW) {
        // Same pdfUrl-resolution precedence as buildDetachableTabData below.
        const config = (dragged.getConfig() ?? {}) as { compiledFile?: string; pdfUrl?: string }
        const effectivePdfUrl = config.compiledFile
          ? (state.pdfByFile[config.compiledFile] ?? config.pdfUrl ?? state.pdfUrl)
          : state.pdfUrl
        ghost = buildPdfDragCard(dragged.getName(), effectivePdfUrl ? state.pdfThumbnailByUrl[effectivePdfUrl] : undefined)
      }
      if (!ghost) return

      suppressNativeDragImage(e.dataTransfer)

      removeDragGhost()
      ghost.classList.add("fl-drag-ghost")
      ghost.style.left = `${e.clientX + 16}px`
      ghost.style.top = `${e.clientY + 16}px`
      ghost.style.pointerEvents = "none"
      ghost.style.willChange = "transform"
      document.body.appendChild(ghost)
      dragGhostRef.current = ghost
    }

    // Keeps the floating ghost following the pointer. 'drag' (fired on the
    // source element) is the more obvious event to use here, but it reports
    // clientX/clientY as 0 in some browsers; 'dragover' — which bubbles to
    // `document` from whatever's under the pointer — reports real
    // coordinates everywhere, so it's used for tracking despite firing on
    // every element the drag passes over rather than just the source.
    const handleDragOverMoveGhost = (e: DragEvent) => {
      const ghost = dragGhostRef.current
      if (!ghost) return
      // Ensure ghost is still in DOM (state updates might remove it)
      if (!document.body.contains(ghost)) {
        document.body.appendChild(ghost)
      }
      ghost.style.left = `${e.clientX + 16}px`
      ghost.style.top = `${e.clientY + 16}px`
    }

    document.addEventListener("dragstart", handleDragStartSetImage)
    document.addEventListener("dragover", handleDragOverMoveGhost)
    document.addEventListener("dragend", removeDragGhost)
    document.addEventListener("drop", removeDragGhost)

    wrapper.addEventListener("dragstart", handleDragStart)
    wrapper.addEventListener("dragend", handleDragEnd)

    return () => {
      wrapper.removeEventListener("dragstart", handleDragStart)
      wrapper.removeEventListener("dragend", handleDragEnd)
      document.removeEventListener("dragstart", handleDragStartSetImage)
      document.removeEventListener("dragover", handleDragOverMoveGhost)
      document.removeEventListener("dragend", removeDragGhost)
      document.removeEventListener("drop", removeDragGhost)
      removeDragGhost()
    }
  }, [model, getContent, setActiveFile, projectId])

  /* ── Directional split-pad: an alternate drop preview shown when a tab is
   * dragged over ANOTHER tabset's content area (not its tab bar, not a
   * tabset header, not the outer layout edge — those keep FlexLayout's own
   * preview since the pointer there never resolves to a `resolveHit`). The
   * pad always renders centered on the hovered panel; a split/merge is only
   * actionable while the pointer sits directly over one of the pad's 5
   * icons — everywhere else (including out at the panel's own edges) is a
   * deliberate no-op.
   *
   * This is purely a visual + model-action layer on top of FlexLayout's own
   * drag handling, which is left completely unsuppressed: an earlier version
   * stopped propagation on dragover/drop to fully replace FlexLayout's
   * handling, but that also silently broke FlexLayout's own dragend-driven
   * cleanup (TabButton's onDragEnd calls `layout.clearDragMain()`, which
   * never ran once 'drop' stopped reaching it — leaving its overlay/outline
   * permanently stuck and blocking every subsequent drag). Instead:
   *  - FlexLayout's own `.flexlayout__outline_rect` is hidden via CSS for
   *    the whole drag (`.fl-tab-dragging`, toggled in the detach-drag effect
   *    above) rather than only while our pad is governing — it was also
   *    showing (non-functionally) over tab bars and layout edges our pad
   *    doesn't cover, so it's suppressed everywhere our own preview isn't.
   *  - FlexLayout still computes and applies its own FlexLayout_MoveNode
   *    action on drop; handleAction (above) rewrites or cancels it using
   *    `pendingSplitHitRef`, which this effect keeps up to date.
   */
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || !model) return

    const overlayRoot = document.createElement("div")
    overlayRoot.className = "fl-split-compass-root"
    const previewRect = document.createElement("div")
    previewRect.className = "fl-split-compass-preview"
    const pad = document.createElement("div")
    pad.className = "fl-split-compass-pad"
    const highlight = document.createElement("div")
    highlight.className = "fl-split-compass-highlight"
    overlayRoot.append(previewRect, pad, highlight)
    overlayRoot.style.display = "none"
    wrapper.appendChild(overlayRoot)

    const hideVisual = () => {
      wrapper.classList.remove("fl-split-compass-governing")
      overlayRoot.style.display = "none"
      previewRect.classList.remove("fl-active")
      highlight.classList.remove("fl-active")
    }

    const hide = () => {
      pendingSplitHitRef.current = null
      hideVisual()
    }

    /**
     * Resolves the hovered tabset's content area, id, and split direction (if
     * any) for a client point. FlexLayout renders a transparent full-layout
     * `.flexlayout__layout_overlay` on top of everything the instant a drag
     * starts (so drags register reliably over iframes like the PDF preview),
     * which means `event.target` is always that overlay, never the real
     * content beneath it — so hit-testing has to look past it via
     * elementsFromPoint rather than trusting the event's own target.
     */
    const resolveHit = (clientX: number, clientY: number) => {
      const stack = wrapper.ownerDocument.elementsFromPoint(clientX, clientY) as HTMLElement[]

      // Search for flexlayout__tabset_content by walking up from the topmost element
      let contentEl: HTMLElement | null = null
      for (const el of stack) {
        if (el.classList.contains("flexlayout__tabset_content")) {
          contentEl = el
          break
        }
        // Also check parents of each element in case content is nested
        let current = el as HTMLElement | null
        while (current && !contentEl) {
          if (current.classList.contains("flexlayout__tabset_content")) {
            contentEl = current
            break
          }
          current = current.parentElement
        }
        if (contentEl) break
      }

      if (!contentEl) {
        return null
      }

      try {
        // Find the actual tabsetId by searching FlexLayout's model
        let tabsetId: string | undefined
        if (model) {
          const walkNodes = (node: any) => {
            if (node.getType?.() === "tabset") {
              // This is a TabSetNode — check if it contains our contentEl
              const allEls = wrapper.querySelectorAll<HTMLElement>(".flexlayout__tabset")
              for (const el of allEls) {
                if (el.contains(contentEl)) {
                  tabsetId = node.getId?.() as string | undefined
                  return true
                }
              }
            }
            // Recursively check children
            const children = node.getChildren?.()
            if (Array.isArray(children)) {
              return children.some(walkNodes)
            }
            return false
          }
          walkNodes(model.getRoot())
        }

        if (!tabsetId) {
          return null
        }

        const rect = contentEl.getBoundingClientRect()
        const cx = rect.left + rect.width / 2
        const cy = rect.top + rect.height / 2
        const localX = clientX - (cx - COMPASS_WIDTH / 2)
        const localY = clientY - (cy - COMPASS_HEIGHT / 2)
        const direction = detectDirection(localX, localY)

        return { tabsetId, rect, cx, cy, direction }
      } catch (err) {
        return null
      }
    }

    // Belt-and-suspenders against FlexLayout's own native outline: the
    // `.fl-tab-dragging` CSS rule (see flexlayout-overrides.css) hides it in
    // the common case, but it's been observed still showing alongside our
    // pad — visible as a second, larger dark rectangle with its own
    // compass icon, positioned independently of ours. Rather than chase the
    // exact cause (a stale outline left over from FlexLayout's own internal
    // drag-state tracking is one candidate — this codebase already notes
    // elsewhere that interfering with its drag lifecycle can leave that
    // state stuck), force every such element's `display` off directly, every
    // frame a drag is in flight, regardless of how many exist or why.
    const suppressNativeOutlines = () => {
      document
        .querySelectorAll<HTMLElement>(".flexlayout__outline_rect, .flexlayout__outline_rect_edge")
        .forEach((el) => el.style.setProperty("display", "none", "important"))
    }

    const processDragOver = (clientX: number, clientY: number) => {
      if (!draggedTabRef.current) {
        return
      }
      suppressNativeOutlines()
      const hit = resolveHit(clientX, clientY)
      if (!hit) {
        hide()
        return
      }

      pendingSplitHitRef.current = { tabsetId: hit.tabsetId, direction: hit.direction }
      wrapper.classList.add("fl-split-compass-governing")

      const wrapperRect = wrapper.getBoundingClientRect()
      overlayRoot.style.display = "block"
      pad.style.left = `${hit.cx - wrapperRect.left}px`
      pad.style.top = `${hit.cy - wrapperRect.top}px`

      if (hit.direction) {
        const zone = ZONE_CENTERS[hit.direction]
        highlight.style.left = `${hit.cx - wrapperRect.left + zone.x - COMPASS_WIDTH / 2}px`
        highlight.style.top = `${hit.cy - wrapperRect.top + zone.y - COMPASS_HEIGHT / 2}px`
        highlight.classList.add("fl-active")

        const panelRect = {
          x: hit.rect.left - wrapperRect.left,
          y: hit.rect.top - wrapperRect.top,
          width: hit.rect.width,
          height: hit.rect.height,
        }
        const preview = dockRectFor(hit.direction, panelRect)
        previewRect.style.left = `${preview.x}px`
        previewRect.style.top = `${preview.y}px`
        previewRect.style.width = `${preview.width}px`
        previewRect.style.height = `${preview.height}px`
        previewRect.classList.add("fl-active")
      } else {
        highlight.classList.remove("fl-active")
        previewRect.classList.remove("fl-active")
      }
    }

    // Store processDragOver in ref so EditorTabBar can call it directly
    compassProcessDragOverRef.current = processDragOver

    // 'dragover' fires far more often than a screen repaints — doing
    // resolveHit's elementsFromPoint + getBoundingClientRect + style writes
    // synchronously on every single event caused visible lag while dragging.
    // Coalesce to at most once per animation frame: only the latest pointer
    // position by the time the frame runs actually gets processed.
    let dragOverRaf = 0
    let latestDragPoint: { x: number; y: number } | null = null
    const cancelPendingDragOver = () => {
      if (dragOverRaf) {
        cancelAnimationFrame(dragOverRaf)
        dragOverRaf = 0
      }
    }

    const handleDragOver = (e: DragEvent) => {
      latestDragPoint = { x: e.clientX, y: e.clientY }
      if (dragOverRaf) return
      dragOverRaf = requestAnimationFrame(() => {
        dragOverRaf = 0
        if (latestDragPoint) {
          processDragOver(latestDragPoint.x, latestDragPoint.y)
        }
      })
    }

    const handleDragLeave = (e: DragEvent) => {
      if (!wrapper.contains(e.relatedTarget as Node | null)) {
        cancelPendingDragOver()
        hide()
      }
    }

    const handleDragEnd = () => {
      cancelPendingDragOver()
      hide()
    }

    // 'dragend' turns out not to be reliable here (confirmed empirically —
    // FlexLayout's own cleanup runs fine via its 'drop' handler regardless,
    // but 'dragend' itself sometimes just never fires), so it can't be the
    // only thing hiding our overlay. Hide the *visual* on 'drop' instead —
    // 'drop' reliably fires. Only the visual, though: `wrapper`'s bubble-
    // phase 'drop' listener fires *before* FlexLayout's own onDrop (bound
    // higher up, so reached later in the bubble), which is what
    // synchronously triggers handleAction's read of pendingSplitHitRef —
    // clearing that ref here would race it away first. The deferred clear
    // below runs after that synchronous dispatch completes, so it only
    // catches drops FlexLayout's onDrop never actually consumed (backup
    // only — the normal case is handleAction already having cleared it).
    const handleDrop = () => {
      cancelPendingDragOver()
      hideVisual()
      setTimeout(() => {
        pendingSplitHitRef.current = null
      }, 0)
    }

    wrapper.addEventListener("dragover", handleDragOver)
    wrapper.addEventListener("dragleave", handleDragLeave)
    wrapper.addEventListener("dragend", handleDragEnd)
    wrapper.addEventListener("drop", handleDrop)

    return () => {
      cancelPendingDragOver()
      wrapper.removeEventListener("dragover", handleDragOver)
      wrapper.removeEventListener("drop", handleDrop)
      wrapper.removeEventListener("dragleave", handleDragLeave)
      wrapper.removeEventListener("dragend", handleDragEnd)
      overlayRoot.remove()
    }
  }, [model])

  /* ── Tab bar horizontal wheel scroll & auto-scroll selected tab into view ── */
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement
      const scrollParent = target.closest<HTMLElement>(".flexlayout__tabset_tabbar_inner")
      if (!scrollParent) return

      if (scrollParent.scrollWidth > scrollParent.clientWidth) {
        e.preventDefault()
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
        scrollParent.scrollLeft += delta
      }
    }

    wrapper.addEventListener("wheel", handleWheel, { passive: false })

    return () => {
      wrapper.removeEventListener("wheel", handleWheel)
    }
  }, [model])

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper || !model) return

    requestAnimationFrame(() => {
      const selectedTabs = wrapper.querySelectorAll<HTMLElement>(".flexlayout__tab_button--selected")
      selectedTabs.forEach((tab) => {
        tab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" })
      })
    })
  }, [model, state.activeFile, state.compiledFile])

  /* ── Hide hidden tabs overflow icon button ── */
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const hideOverflow = () => {
      wrapper
        .querySelectorAll<HTMLElement>(
          ".flexlayout__tab_button_overflow, " +
          ".flexlayout__tab_button_overflow_count, " +
          ".flexlayout__tab_toolbar_button-overflow, " +
          ".flexlayout__tab_toolbar_button_overflow, " +
          "button[title*='overflow' i], " +
          "button[title*='hidden tab' i], " +
          "button[title*='Hidden tab' i]"
        )
        .forEach((el) => el.style.setProperty("display", "none", "important"))
    }

    hideOverflow()
    const observer = new MutationObserver(hideOverflow)
    observer.observe(wrapper, { childList: true, subtree: true, attributes: true })

    return () => observer.disconnect()
  }, [model])



  /* ── SSR guard ── */
  if (!model) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        Loading layout…
      </div>
    )
  }

  /* ── theme class for flexlayout ── */
  const themeClass =
    resolvedTheme === "dark"
      ? "flexlayout__theme_dark"
      : "flexlayout__theme_light"

  return (
    <div ref={wrapperRef} className={`relative flex-1 min-h-0 min-w-0 ${themeClass}`}>
      <Layout
        ref={layoutRef}
        model={model}
        factory={factory}
        onModelChange={handleModelChange}
        onAction={handleAction}
        onRenderTabSet={handleRenderTabSet}
        realtimeResize
      />
    </div>
  )
}
